import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Check, X, Clock, Phone, Sparkles, ChevronRight,
} from "lucide-react";

const STATUS_STYLES = {
  pending:   { label: "Pending",   bg: "bg-amber-50",   text: "text-amber-800",   border: "border-amber-200" },
  accepted:  { label: "Accepted",  bg: "bg-blue-50",    text: "text-blue-800",    border: "border-blue-200" },
  paid:      { label: "Paid",      bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200" },
  completed: { label: "Completed", bg: "bg-slate-100",  text: "text-slate-700",   border: "border-slate-200" },
  declined:  { label: "Declined",  bg: "bg-rose-50",    text: "text-rose-800",    border: "border-rose-200" },
  cancelled: { label: "Cancelled", bg: "bg-slate-50",   text: "text-slate-500",   border: "border-slate-200" },
};

export default function AdminMentorCalls() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/mentor-call/inquiries${statusFilter ? `?status=${statusFilter}` : ""}`);
      setRows(r.data);
    } catch { toast.error("Couldn't load inquiries."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  return (
    <div data-testid="admin-mentor-calls" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-amber-600 font-semibold mb-1">— Admin</div>
        <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight">
          Mentor call inquiries
        </h1>
        <p className="mt-1 text-sm text-slate-500 max-w-xl">
          Review requested slots, accept one that works, or decline with a note. Students only pay after you accept.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
        {["pending", "accepted", "paid", "completed", "declined", "cancelled", ""].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            data-testid={`admin-mentor-filter-${s || "all"}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
              statusFilter === s
                ? "bg-blue-800 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {s ? STATUS_STYLES[s]?.label || s : "All"}
          </button>
        ))}
      </div>

      {loading && <div className="text-slate-500 text-sm">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500">
          No inquiries in this bucket.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => (
          <InquiryCard key={r.id} inquiry={r} onChange={load} />
        ))}
      </div>
    </div>
  );
}

function InquiryCard({ inquiry, onChange }) {
  const style = STATUS_STYLES[inquiry.status] || STATUS_STYLES.pending;
  const [pickedSlot, setPickedSlot] = useState(inquiry.proposed_slots[0] || "");
  const [adminNote, setAdminNote] = useState(inquiry.admin_note || "");
  const [busy, setBusy] = useState(null);

  const decide = async (action) => {
    setBusy(action);
    try {
      await api.put(`/admin/mentor-call/inquiries/${inquiry.id}`, {
        action,
        chosen_slot: action === "accept" ? pickedSlot : undefined,
        admin_note: adminNote,
      });
      toast.success(action === "accept" ? "Slot accepted." : "Inquiry declined.");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong.");
    } finally { setBusy(null); }
  };

  const markCompleted = async () => {
    setBusy("complete");
    try {
      await api.post(`/admin/mentor-call/inquiries/${inquiry.id}/complete`);
      toast.success("Marked completed.");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't mark complete.");
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <div className="font-display font-semibold text-slate-900">
            {inquiry.user?.name || inquiry.user_id}
          </div>
          <div className="text-xs text-slate-500">
            {inquiry.user?.email} · <span className="uppercase font-semibold">{inquiry.user?.tier}</span> · submitted {new Date(inquiry.created_at).toLocaleString()}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded border ${style.bg} ${style.text} ${style.border}`}>
          {style.label}
        </span>
      </div>

      {inquiry.notes && (
        <div className="mt-1 rounded-lg bg-slate-50 border border-slate-100 p-3 text-sm text-slate-700 italic">
          "{inquiry.notes}"
        </div>
      )}

      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
          Proposed slots · student timezone: {inquiry.timezone}
        </div>
        {inquiry.status === "pending" ? (
          <div className="space-y-2">
            {inquiry.proposed_slots.map((slot, i) => (
              <label
                key={i}
                data-testid={`admin-mentor-slot-${inquiry.id}-${i}`}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                  pickedSlot === slot
                    ? "border-emerald-400 bg-emerald-50/60"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name={`slot-${inquiry.id}`}
                  checked={pickedSlot === slot}
                  onChange={() => setPickedSlot(slot)}
                  className="accent-emerald-600"
                />
                <span className="text-sm text-slate-800 font-mono">
                  {new Date(slot).toLocaleString([], {
                    weekday: "short", month: "short", day: "numeric",
                    hour: "numeric", minute: "2-digit",
                  })}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-700 font-mono">
            {inquiry.chosen_slot ? (
              <><Check size={13} className="inline mr-1 text-emerald-600" /> {new Date(inquiry.chosen_slot).toLocaleString()}</>
            ) : (
              <span className="text-slate-400">— no slot chosen —</span>
            )}
          </div>
        )}
      </div>

      {inquiry.status === "pending" && (
        <>
          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">
              Note to student (optional)
            </label>
            <Textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows={2}
              placeholder="Add a note the student will see when they pay…"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => decide("decline")}
              disabled={busy !== null}
              data-testid={`admin-mentor-decline-${inquiry.id}`}
              className="border-rose-200 text-rose-700 hover:bg-rose-50"
            >
              {busy === "decline" ? "…" : <><X size={13} className="mr-1" /> Decline</>}
            </Button>
            <Button
              onClick={() => decide("accept")}
              disabled={busy !== null || !pickedSlot}
              data-testid={`admin-mentor-accept-${inquiry.id}`}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {busy === "accept" ? "…" : <><Check size={13} className="mr-1" /> Accept slot</>}
            </Button>
          </div>
        </>
      )}

      {inquiry.status === "paid" && (
        <div className="mt-3 flex justify-end">
          <Button
            onClick={markCompleted}
            disabled={busy !== null}
            className="bg-blue-800 hover:bg-blue-700 text-white"
          >
            {busy === "complete" ? "…" : <><Check size={13} className="mr-1" /> Mark completed</>}
          </Button>
        </div>
      )}
    </div>
  );
}
